import { FC, FormEvent, useCallback, useState } from "react";
import {
    PaymentElement,
    useElements,
    useStripe,
} from "@stripe/react-stripe-js";
import { PendingOrgBillingTransferRequest } from "@common/dto/billing/org-billing-transfer-request.dto";
import { getApiErrorStatus } from "@api/apiError";
import {
    useAcceptOrgBillingTransferMutation,
    useOrgBillingTransferCheckoutMutation,
} from "@api/orgBillingTransferRecipient.api";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { StandardModalBody } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import styles from "./AcceptOrgBillingTransfer.module.scss";
import {
    buildOrgBillingTransferReturnUrl,
    SAVE_PAYMENT_METHOD_ERROR,
} from "./orgBillingTransferReturn.lib";

interface AcceptOrgBillingTransferFormProps {
    request: PendingOrgBillingTransferRequest;
    onClose: () => void;
}

export const AcceptOrgBillingTransferForm: FC<
    AcceptOrgBillingTransferFormProps
> = ({ request, onClose }) => {
    const stripe = useStripe();
    const elements = useElements();
    const { success, error: showError } = useToaster();
    const checkout = useOrgBillingTransferCheckoutMutation();
    const accept = useAcceptOrgBillingTransferMutation();
    const [isBusy, setIsBusy] = useState(false);

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!stripe || !elements || isBusy) {
                return;
            }

            setIsBusy(true);

            try {
                const submitResult = await elements.submit();

                if (submitResult.error) {
                    showError(
                        submitResult.error.message ?? SAVE_PAYMENT_METHOD_ERROR,
                    );
                    return;
                }

                const { intentId, clientSecret } = await checkout.mutateAsync(
                    request.id,
                );
                const { error } = await stripe.confirmSetup({
                    elements,
                    clientSecret,
                    confirmParams: {
                        return_url: buildOrgBillingTransferReturnUrl(
                            request.id,
                        ),
                    },
                    redirect: "if_required",
                });

                if (error) {
                    showError(error.message ?? SAVE_PAYMENT_METHOD_ERROR);
                    return;
                }

                await accept.mutateAsync({ requestId: request.id, intentId });
                success("Billing transfer accepted");
                onClose();
            } catch (error) {
                if (getApiErrorStatus(error) === undefined) {
                    showError("Failed to accept billing transfer");
                }
            } finally {
                setIsBusy(false);
            }
        },
        [
            accept,
            checkout,
            elements,
            isBusy,
            onClose,
            request.id,
            showError,
            stripe,
            success,
        ],
    );

    return (
        <StandardModalBody>
            <form onSubmit={handleSubmit}>
                <p className={styles.description}>
                    {request.organizationName} asked you to take over billing
                    for this workspace. Add a payment method to accept.
                </p>
                <section className={styles.paymentElement}>
                    <PaymentElement />
                </section>
                <footer className={styles.footer}>
                    <ButtonWithLoader
                        variant="primary"
                        size="md"
                        type="submit"
                        loading={isBusy}
                        disabled={isBusy}
                    >
                        Accept and set up billing
                    </ButtonWithLoader>
                </footer>
            </form>
        </StandardModalBody>
    );
};
